import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { FaArrowLeft, FaFilePdf, FaSave } from "react-icons/fa";
import { MdPrint, MdOpenInNew, MdDelete } from "react-icons/md";
import { useReactToPrint } from "react-to-print";
import { fetchCustomers } from "../../store/Slices/CustomerSlice";
import { fetchCustomerLedger } from "../../store/Slices/CustomerLedgerSlice";
import { CustomerItemSummaryColumns } from "../../assets/Columns/CustomerItemSummaryColumns";
import { fetchCustomerItemSummary } from "../../store/Slices/CustomerItemSummarySlice";
import FigureCard from "../Cards/FigureCard";
import DetailsCard from "../Cards/DetailsCard";
import ThermalItemSummary from "./ThermalItemSummary";
import ThermalPrintHost from "../Thermal/ThermalPrintHost";
import CustomerItemSummaryCards from "./CustomerItemSummaryCards";
import CustomerItemSummaryA4Document from "./CustomerItemSummaryA4Document";
import { mapItemSummaryToThermalReceipt } from "../../utils/thermalReceipt";
import { THERMAL_WIDTH_MM } from "../Thermal/thermalConfig";
import {
  A4_PRINT_PAGE_STYLE,
  downloadElementAsA4Pdf,
} from "../../utils/downloadElementAsA4Pdf";
import { usePageRefresh } from "../../hooks/usePageRefresh";
import CustomModal from "../Modals/CustomModal";
import {
  CreateCustomerItemSummaryRecordApi,
  DeleteCustomerItemSummaryRecordApi,
  GetCustomerItemSummaryRecordsApi,
} from "../../ApiRequests";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { formatDateTime } from "../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user") || "{}");
const storedBranch = localStorage.getItem("branch");
const sessionBranchId =
  userData.role === 2
    ? userData.branchId
    : userData.branchId || storedBranch || "";

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const formatRecordWhen = (record) => {
  if (record?.date) return formatDateTime(record.date);
  if (record?.createdAt) return formatDateTime(record.createdAt);
  return "—";
};

const getShoppingAmount = (shRows) => {
  if (!shRows?.length) return 0;
  const shoppingRow = shRows.find(
    (dt) => dt.name === "Shopping" || dt.itemId === "12345678"
  );
  if (shoppingRow) return Number(shoppingRow.amount || 0);
  return Number(shRows[shRows.length - 2]?.amount || 0);
};

const getShoppingAmountFromAll = (rows) => {
  if (!rows?.length) return 0;
  return rows
    .filter((dt) => dt.type === "SH" && dt.name !== "Total")
    .reduce((acc, dt) => acc + Number(dt.amount || 0), 0);
};

const isSameSummaryRow = (a, b) => {
  if (a.name === "Shopping" || b.name === "Shopping") {
    return a.name === "Shopping" && b.name === "Shopping";
  }
  if (a.name === "Total" || b.name === "Total") {
    return a.name === "Total" && b.name === "Total";
  }
  const aId = a.itemId?._id || a.itemId;
  const bId = b.itemId?._id || b.itemId;
  return String(aId) === String(bId);
};

const recalcShTable = (rows, changedRow, updatedItem) => {
  const mapDataSh = rows.map((dt) =>
    isSameSummaryRow(dt, changedRow) ? updatedItem : dt
  );

  const totalQty = mapDataSh
    .slice(0, -1)
    .reduce((total, dt) => total + Number(dt.qty || 0), 0);

  const totalAmount = mapDataSh
    .slice(0, -1)
    .reduce((total, dt) => total + Number(dt.amount || 0), 0);

  return mapDataSh.map((dt, index) => {
    if (isSameSummaryRow(dt, changedRow)) return updatedItem;
    if (index === mapDataSh.length - 1) {
      return { ...dt, qty: totalQty, amount: totalAmount };
    }
    return dt;
  });
};

const recalcAllTable = (rows, changedRow, updatedItem) => {
  const mapData = rows.map((dt) =>
    isSameSummaryRow(dt, changedRow) ? updatedItem : dt
  );

  const totalQty = mapData
    .slice(0, -1)
    .reduce((total, dt) => total + Number(dt.qty || 0), 0);

  const totalAmount = mapData
    .slice(0, -1)
    .reduce((total, dt) => total + Number(dt.amount || 0), 0);

  return mapData.map((dt, index) => {
    if (isSameSummaryRow(dt, changedRow)) return updatedItem;
    if (index === mapData.length - 1) {
      return { ...dt, qty: totalQty, amount: totalAmount };
    }
    return dt;
  });
};

const syncShoppingRowInShData = (shRows, shoppingAmount) => {
  if (!shRows?.length) return shRows;

  const nextRows = shRows.map((dt) => {
    if (dt.name === "Shopping" || dt.itemId === "12345678") {
      return { ...dt, price: shoppingAmount, amount: shoppingAmount };
    }
    return dt;
  });

  const totalQty = nextRows
    .slice(0, -1)
    .reduce((total, dt) => total + Number(dt.qty || 0), 0);

  const totalAmount = nextRows
    .slice(0, -1)
    .reduce((total, dt) => total + Number(dt.amount || 0), 0);

  return nextRows.map((dt, index) =>
    index === nextRows.length - 1
      ? { ...dt, qty: totalQty, amount: totalAmount }
      : dt
  );
};

const CustomersItemSummaryReport = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [CurrentSummaryTab, setCurrentSummaryTab] = useState("All");
  const [prices, setPrices] = useState({});
  const [updatedData, setUpdatedData] = useState(null);
  const [updatedDataSh, setUpdatedDataSh] = useState(null);
  const [shoppingTotal, setShoppingTotal] = useState(0);
  const [showThermalPrice, setShowThermalPrice] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [openRecordModal, setOpenRecordModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordPdfLoading, setRecordPdfLoading] = useState(false);
  /** When set, top table shows this saved snapshot (editable). */
  const [activeSavedId, setActiveSavedId] = useState(null);
  const [activeSavedMeta, setActiveSavedMeta] = useState(null);
  const loadedSummaryIdRef = useRef(null);
  const recordPrintRef = useRef(null);
  const CustomerLedgers = useSelector((state) => state.CustomerLedgers);
  const CustomerItemSummaryState = useSelector(
    (state) => state.CustomerItemSummaryState
  );
  const navigate = useNavigate();
  const Customers = useSelector((state) => state.Customers);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      loadedSummaryIdRef.current = null;
      setActiveSavedId(null);
      setActiveSavedMeta(null);
      dispatch(fetchCustomerLedger(id));
      dispatch(fetchCustomerItemSummary(id));
    }
  }, [id, dispatch]);

  const refreshSummary = useCallback(() => {
    if (!id) return;
    loadedSummaryIdRef.current = null;
    setActiveSavedId(null);
    setActiveSavedMeta(null);
    dispatch(fetchCustomers());
    dispatch(fetchCustomerLedger(id));
    dispatch(fetchCustomerItemSummary(id));
  }, [id, dispatch]);

  const loadSavedRecords = useCallback(async () => {
    if (!id) return;
    setRecordsLoading(true);
    try {
      const response = await GetCustomerItemSummaryRecordsApi(id);
      if (response.data?.success) {
        setSavedRecords(response.data.data.payload || []);
      } else {
        setSavedRecords([]);
      }
    } catch (err) {
      console.error(err);
      setSavedRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSavedRecords();
  }, [loadSavedRecords]);

  usePageRefresh(() => {
    refreshSummary();
    loadSavedRecords();
  }, [id]);

  useEffect(() => {
    const payload = CustomerItemSummaryState?.data;
    if (!id || !payload?.data?.length || !payload?.shdata?.length) return;
    if (loadedSummaryIdRef.current === id) return;
    // Don't overwrite while a saved snapshot is open for editing
    if (activeSavedId) return;

    loadedSummaryIdRef.current = id;
    setUpdatedData(payload.data);
    setUpdatedDataSh(payload.shdata);
    setShoppingTotal(getShoppingAmount(payload.shdata));
  }, [id, CustomerItemSummaryState?.data, activeSavedId]);

  const hydrateLiveSummary = useCallback(() => {
    const payload = CustomerItemSummaryState?.data;
    if (payload?.data?.length) setUpdatedData(payload.data);
    if (payload?.shdata?.length) {
      setUpdatedDataSh(payload.shdata);
      setShoppingTotal(getShoppingAmount(payload.shdata));
    }
  }, [CustomerItemSummaryState?.data]);

  const totalShoppingAmount = shoppingTotal;

  const totalAmount =
    CurrentSummaryTab === "All"
      ? useMemo(() => {
          return (
            (updatedData?.[updatedData?.length - 1]?.amount || 0) +
            (Customers?.data?.find((dt) => dt._id === id)?.return_amount || 0) +
            (Customers?.data?.find((dt) => dt._id === id)?.discount || 0) +
            // (Customers?.data?.find((dt) => dt._id === id)?.recieved || 0) +
            (Customers?.data?.find((dt) => dt._id === id)?.opening_balance || 0)
          );
        }, [updatedData, Customers?.data, id])
      : CurrentSummaryTab === "SH" &&
        useMemo(() => {
          return (
            (updatedDataSh?.[updatedDataSh?.length - 1]?.amount || 0) +
            (Customers?.data?.find((dt) => dt._id === id)?.return_amount || 0) +
            (Customers?.data?.find((dt) => dt._id === id)?.discount || 0) +
            // (Customers?.data?.find((dt) => dt._id === id)?.recieved || 0) +
            (Customers?.data?.find((dt) => dt._id === id)?.opening_balance || 0)
          );
        }, [updatedDataSh, Customers?.data, id]);

  const totalReciveableAmount = useMemo(() => {
    return (
      totalAmount -
      (Customers?.data?.find((dt) => dt._id === id)?.return_amount || 0) -
      (Customers?.data?.find((dt) => dt._id === id)?.discount || 0) -
      (Customers?.data?.find((dt) => dt._id === id)?.recieved || 0)
    );
  }, [totalAmount, Customers?.data, id]);

  const customerRecord = Customers?.data?.find((dt) => dt._id === id);

  const a4Rows =
    CurrentSummaryTab === "All" ? updatedData || [] : updatedDataSh || [];

  const a4Stats = useMemo(() => {
    const boxSource =
      CurrentSummaryTab === "All"
        ? CustomerItemSummaryState?.data?.data
        : CustomerItemSummaryState?.data?.shdata;
    const totalBox =
      boxSource
        ?.filter((dt) => dt.type === "Box")
        .reduce((acc, dt) => acc + Number(dt.qty || 0), 0) || 0;

    return {
      totalBox,
      totalShopping: totalShoppingAmount,
      openingBalance: customerRecord?.opening_balance,
      discount: customerRecord?.discount,
      returnAmount: customerRecord?.return_amount,
      totalAmount,
      paid: customerRecord?.recieved,
      remaining: totalReciveableAmount,
    };
  }, [
    CurrentSummaryTab,
    CustomerItemSummaryState?.data,
    totalShoppingAmount,
    customerRecord,
    totalAmount,
    totalReciveableAmount,
  ]);

  const contentToPrint = useRef(null);
  const a4PrintRef = useRef(null);
  const thermalPrintRef = useRef(null);
  const [thermalPrintData, setThermalPrintData] = useState(null);
  const printPendingRef = useRef(false);

  const handleA4Print = useReactToPrint({
    content: () => a4PrintRef.current,
    documentTitle: `Customer-Item-Summary-${customerRecord?.name || id}`,
    pageStyle: A4_PRINT_PAGE_STYLE,
  });

  const handleDownloadPdf = async () => {
    if (!a4PrintRef.current) return;
    setPdfLoading(true);
    try {
      await downloadElementAsA4Pdf(
        a4PrintRef.current,
        `customer-item-summary-${customerRecord?.name || id}`
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const resolveBranchId = () => {
    const fromCustomer = customerRecord?.branchId;
    if (fromCustomer?._id) return String(fromCustomer._id);
    if (fromCustomer) return String(fromCustomer);
    if (sessionBranchId?._id) return String(sessionBranchId._id);
    return String(sessionBranchId || "");
  };

  const handleSaveRecord = async () => {
    const rows = a4Rows;
    if (!rows?.length) {
      WarningToast("No summary rows to save");
      return;
    }
    const branchId = resolveBranchId();
    if (!branchId) {
      WarningToast("Branch is required to save record");
      return;
    }

    setSaveLoading(true);
    try {
      const response = await CreateCustomerItemSummaryRecordApi({
        customerId: id,
        branchId,
        tab: CurrentSummaryTab,
        customerSnapshot: {
          name: customerRecord?.name || "",
          contact:
            customerRecord?.contact != null
              ? String(customerRecord.contact)
              : "",
          address: customerRecord?.address || "",
        },
        rows,
        stats: a4Stats,
        totalAmount: Number(totalAmount) || 0,
        remainingAmount: Number(totalReciveableAmount) || 0,
        date: Math.floor(Date.now() / 1000),
      });

      if (response.data?.success) {
        SuccessToast("Summary record saved");
        await loadSavedRecords();
      } else {
        ErrorToast(response.data?.error?.msg || "Failed to save record");
      }
    } catch (err) {
      ErrorToast(err?.response?.data?.error?.msg || "Failed to save record");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleOpenRecord = (record) => {
    if (!record) return;
    const rows = Array.isArray(record.rows)
      ? JSON.parse(JSON.stringify(record.rows))
      : [];
    if (!rows.length) {
      WarningToast("This saved record has no rows to edit");
      return;
    }

    const tab = record.tab === "SH" ? "SH" : "All";
    setCurrentSummaryTab(tab);
    setActiveSavedId(record._id);
    setActiveSavedMeta(record);
    setSelectedRecord(record);

    if (tab === "SH") {
      setUpdatedDataSh(rows);
      setShoppingTotal(getShoppingAmount(rows));
    } else {
      setUpdatedData(rows);
      setShoppingTotal(getShoppingAmountFromAll(rows));
    }

    setOpenRecordModal(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    SuccessToast("Saved summary opened — edit prices above, then Save again");
  };

  const handleBackToLiveSummary = () => {
    setActiveSavedId(null);
    setActiveSavedMeta(null);
    hydrateLiveSummary();
  };

  const handlePrintRecord = useReactToPrint({
    content: () => recordPrintRef.current,
    documentTitle: `Customer-Item-Summary-Record-${
      selectedRecord?.customerSnapshot?.name || id
    }`,
    pageStyle: A4_PRINT_PAGE_STYLE,
  });

  const handleDownloadRecordPdf = async () => {
    if (!recordPrintRef.current || !selectedRecord) return;
    setRecordPdfLoading(true);
    try {
      await downloadElementAsA4Pdf(
        recordPrintRef.current,
        `customer-item-summary-${selectedRecord.customerSnapshot?.name || id}-${
          selectedRecord._id
        }`
      );
    } finally {
      setRecordPdfLoading(false);
    }
  };

  const handleDeleteRecord = async (record) => {
    if (!record?._id) return;
    try {
      const response = await DeleteCustomerItemSummaryRecordApi(record._id);
      if (response.data?.success) {
        SuccessToast("Record deleted");
        if (selectedRecord?._id === record._id) {
          setOpenRecordModal(false);
          setSelectedRecord(null);
        }
        if (activeSavedId === record._id) {
          handleBackToLiveSummary();
        }
        await loadSavedRecords();
      } else {
        ErrorToast(response.data?.error?.msg || "Failed to delete record");
      }
    } catch (err) {
      ErrorToast(err?.response?.data?.error?.msg || "Failed to delete record");
    }
  };

  const pendingRecordDownloadRef = useRef(false);

  const handleDownloadRecordFromTable = (record) => {
    pendingRecordDownloadRef.current = true;
    setSelectedRecord(record);
    setOpenRecordModal(true);
  };

  useEffect(() => {
    if (!pendingRecordDownloadRef.current || !selectedRecord) return;
    const timer = setTimeout(async () => {
      if (!pendingRecordDownloadRef.current) return;
      pendingRecordDownloadRef.current = false;
      if (recordPrintRef.current) {
        await handleDownloadRecordPdf();
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [selectedRecord]);

  const handleAllPriceChange = (item, newPrice) => {
    const updatedItem = {
      ...item,
      price: newPrice,
      amount: newPrice * item.qty,
    };
    const mapUpdatedData = recalcAllTable(updatedData, item, updatedItem);
    setUpdatedData(mapUpdatedData);
    if (item.type === "SH" && item.name !== "Total") {
      const nextShoppingTotal = getShoppingAmountFromAll(mapUpdatedData);
      setShoppingTotal(nextShoppingTotal);
      if (updatedDataSh?.length) {
        setUpdatedDataSh(syncShoppingRowInShData(updatedDataSh, nextShoppingTotal));
      }
    }
  };

  const handleShPriceChange = (item, newPrice) => {
    const updatedItem = {
      ...item,
      price: newPrice,
      amount: newPrice * item.qty,
    };
    const mapUpdatedDataSh = recalcShTable(updatedDataSh, item, updatedItem);
    setUpdatedDataSh(mapUpdatedDataSh);
    setShoppingTotal(getShoppingAmount(mapUpdatedDataSh));
  };

  const buildThermalSummaryData = useCallback(() => {
    const rows =
      CurrentSummaryTab === "All" ? updatedData : updatedDataSh;
    const boxTotal =
      CurrentSummaryTab === "All"
        ? CustomerItemSummaryState?.data?.data
            ?.filter((dt) => dt.type === "Box")
            .reduce((acc, dt) => acc + Number(dt.qty || 0), 0) || 0
        : CustomerItemSummaryState?.data?.shdata
            ?.filter((dt) => dt.type === "Box")
            .reduce((acc, dt) => acc + Number(dt.qty || 0), 0) || 0;

    return mapItemSummaryToThermalReceipt({
      title: "Customer Item Summary",
      partyLabel: "Customer",
      partyName: customerRecord?.name || "",
      partyContact: customerRecord?.contact || "",
      partyAddress: customerRecord?.address || "",
      tabLabel: CurrentSummaryTab,
      showPrice: showThermalPrice,
      rows: rows || [],
      footer: {
        totalAmount,
        openingBalance: customerRecord?.opening_balance,
        discount: customerRecord?.discount,
        returnAmount: customerRecord?.return_amount,
        paid: customerRecord?.recieved,
        remaining: totalReciveableAmount,
        totalBox: boxTotal,
        totalShopping: totalShoppingAmount,
      },
    });
  }, [
    CurrentSummaryTab,
    showThermalPrice,
    updatedData,
    updatedDataSh,
    customerRecord,
    totalAmount,
    totalReciveableAmount,
    totalShoppingAmount,
    CustomerItemSummaryState?.data,
  ]);

  const handleThermalPrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `ItemSummary-${customerRecord?.name || id}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      printPendingRef.current = false;
    },
  });

  useEffect(() => {
    if (!printPendingRef.current || !thermalPrintData) return;
    printPendingRef.current = false;
    const timer = setTimeout(() => handleThermalPrint(), 350);
    return () => clearTimeout(timer);
  }, [thermalPrintData, handleThermalPrint]);

  const AmountTitleConverter = (amoutTitle) => {
    console.log(amoutTitle);

    switch (amoutTitle) {
      case "Payable":
        return "Remaining";
      case "Paid":
        return "Paid";
      case "Total":
        return "Total";
      default:
        return amoutTitle;
    }
  };

  const [CurrentTab, setCurrentTab] = useState("All");

  const CurrentData =
    CurrentTab === "All"
      ? CustomerLedgers.data.All
      : CurrentTab === "Item"
      ? CustomerLedgers.data.Item
      : CurrentTab === "Cash"
      ? CustomerLedgers.data.Cash
      : [];

  return (
    <div className="flex flex-col items-center w-full max850:px-2 pb-6">
      <div className="w-full max850:sticky max850:top-14 z-20 bg-card/95 backdrop-blur-sm border-b border-line py-3 mb-4 px-3 max850:px-2">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            className="max850:hidden border-2 border-ink px-3 py-2 rounded-full hover:bg-ink hover:text-white transition-colors"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <FaArrowLeft />
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-ink bg-ink text-white text-xs font-bold min-w-[120px]"
            onClick={() => handleA4Print()}
          >
            <MdPrint size={16} />
            Print A4
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-main bg-main text-white text-xs font-bold min-w-[120px]"
            onClick={() => {
              printPendingRef.current = true;
              setThermalPrintData(buildThermalSummaryData());
            }}
          >
            <MdPrint size={16} />
            Thermal {THERMAL_WIDTH_MM}mm
          </button>
          <button
            type="button"
            disabled={pdfLoading}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-danger bg-danger text-white text-xs font-bold min-w-[120px] disabled:opacity-60"
            onClick={handleDownloadPdf}
          >
            <FaFilePdf size={14} />
            {pdfLoading ? "PDF…" : "Download PDF"}
          </button>
          <button
            type="button"
            disabled={saveLoading}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-success bg-success text-white text-xs font-bold min-w-[120px] disabled:opacity-60"
            onClick={handleSaveRecord}
          >
            <FaSave size={14} />
            {saveLoading ? "Saving…" : "Save Record"}
          </button>
        </div>
        <label className="mt-2 flex items-center justify-center gap-2 text-[11px] font-semibold text-inkSecondary cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showThermalPrice}
            onChange={(e) => setShowThermalPrice(e.target.checked)}
            className="h-3.5 w-3.5 accent-main"
          />
          Show price on thermal print
        </label>
      </div>

      {activeSavedId && (
        <div className="w-full max-w-5xl mb-3 px-2">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-main/30 bg-brandSoft px-4 py-2.5">
            <p className="text-sm text-ink">
              Viewing saved summary
              {activeSavedMeta
                ? ` · ${formatRecordWhen(activeSavedMeta)} · Tab ${
                    activeSavedMeta.tab || "All"
                  }`
                : ""}
              . Edit prices above, then Save Record again.
            </p>
            <button
              type="button"
              onClick={handleBackToLiveSummary}
              className="text-xs font-bold text-main hover:underline shrink-0"
            >
              Back to live summary
            </button>
          </div>
        </div>
      )}

      <div className="flex w-full rounded-xl overflow-hidden bg-subtle border-2 border-main">
        {["All", "SH"].map((tb) => {
          return (
            <div
              key={tb}
              className={`text-center py-3 font-bold transition-all ease-in-out duration-700 cursor-pointer w-full font-quicksand  ${
                CurrentSummaryTab === tb ? "bg-main text-white" : "text-inkSecondary"
              } `}
              onClick={() => {
                setCurrentSummaryTab(tb);
              }}
            >
              {tb}
            </div>
          );
        })}
      </div>

      <div ref={contentToPrint} className="flex flex-col w-full max-w-5xl p-2">
        <img
          src={`/banner.jpeg`}
          alt="banner"
          className="w-[100%] h-auto border-2 border-[#002650] rounded-t-lg"
        />
        {/* customer details */}
        <div className="flex w-full bg-main text-white justify-center items-center py-2 font-bold text-xl px-3 font-quicksand mt-1">
          Customer Details
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 border-2 border-main rounded-none">
          <DetailsCard
            title={"Name"}
            value={Customers.data.find((dt) => dt._id === id)?.name || ""}
          />
          <DetailsCard
            title={"Contact"}
            value={Customers.data.find((dt) => dt._id === id)?.contact || ""}
          />
          <DetailsCard
            title={"Address"}
            value={Customers.data.find((dt) => dt._id === id)?.address || ""}
          />
        </div>
        <div className="flex w-full bg-main text-white justify-center items-center py-2 font-bold text-xl px-3 font-quicksand mt-1">
          Customer Item Summary
        </div>

        {/* Mobile cards */}
        <div className="mt-2 w-full hidden max850:block">
          {CurrentSummaryTab === "All" && updatedData && (
            <CustomerItemSummaryCards
              rows={updatedData}
              onPriceChange={handleAllPriceChange}
            />
          )}
          {CurrentSummaryTab === "SH" && updatedDataSh && (
            <CustomerItemSummaryCards
              rows={updatedDataSh}
              onPriceChange={handleShPriceChange}
            />
          )}
        </div>

        {/* Desktop + print/PDF table */}
        {CustomerItemSummaryState?.data?.data &&
          CustomerItemSummaryState.data.data.length > 0 && (
            <div className="w-full overflow-x-auto mt-1 block max850:hidden">
              <table className="w-full text-left border-collapse border border-main">
                <thead className="bg-main text-white">
                  <tr>
                    {CustomerItemSummaryColumns.filter(
                      (dt) => dt.id !== "actions" && dt.id !== "description"
                    ).map((dt, ci) => {
                      return (
                        <th
                          className={`border border-main px-3 py-2 whitespace-nowrap ${
                            ci === 0
                              ? "flex-1"
                              : ci === 1
                              ? "w-[120px]"
                              : ci === 2
                              ? "w-[120px]"
                              : ci === 3
                              ? "w-[120px]"
                              : "w-[120px]"
                          }`}
                        >
                          {["payable", "paid", "total"].includes(dt.id)
                            ? AmountTitleConverter(dt.title)
                            : dt.id === "price"
                            ? "Price"
                            : dt.title}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {updatedData && CurrentSummaryTab === "All"
                    ? updatedData.map((item, index) => {
                        const isLastRow = index === updatedData.length - 1;

                        return (
                          <tr
                            key={index}
                            className={`text-main ${
                              isLastRow
                                ? "bg-main text-white font-bold"
                                : ""
                            }`}
                          >
                            {CustomerItemSummaryColumns.filter(
                              (dt) =>
                                dt.id !== "actions" && dt.id !== "description"
                            ).map((dt, i) => (
                              <td
                                key={i}
                                className={`border border-main px-3 py-2 ${
                                  ["payable", "paid", "total"].includes(dt.id)
                                    ? "font-bold"
                                    : ""
                                } ${isLastRow ? "text-white" : ""}`}
                              >
                                {dt.id === "price" && !isLastRow ? (
                                  <input
                                    type="number"
                                    value={item.price}
                                    onChange={(e) =>
                                      handleAllPriceChange(
                                        item,
                                        Number(e.target.value)
                                      )
                                    }
                                    className="w-full px-2 py-1 border-none bg-transparent outline-none rounded"
                                  />
                                ) : [
                                    "payable",
                                    "cr",
                                    "dr",
                                    "price",
                                    "paid",
                                    "total",
                                  ].includes(dt.id) && !isLastRow ? (
                                  `${Number(
                                    updatedData?.[item._id]?.[dt.id] ||
                                      item[dt.id]
                                  ).toLocaleString("en-PK")}`
                                ) : dt.id === "companyId" ? (
                                  item.companyId.name
                                ) : dt.id === "date" ? (
                                  formatDateTime(item[dt.id])
                                ) : (
                                  item[dt.id]
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    : CurrentSummaryTab === "SH" &&
                      updatedDataSh &&
                      updatedDataSh.map((item, index) => {
                        const isLastRow = index === updatedDataSh?.length - 1;

                        return (
                          <tr
                            key={index}
                            className={`text-main ${
                              isLastRow
                                ? "bg-main text-white font-bold"
                                : ""
                            }`}
                          >
                            {CustomerItemSummaryColumns.filter(
                              (dt) =>
                                dt.id !== "actions" && dt.id !== "description"
                            ).map((dt, i) => (
                              <td
                                key={i}
                                className={`border border-main px-3 py-2 ${
                                  ["payable", "paid", "total"].includes(dt.id)
                                    ? "font-bold"
                                    : ""
                                } ${isLastRow ? "text-white" : ""}`}
                              >
                                {dt.id === "price" && !isLastRow ? (
                                  <input
                                    type="number"
                                    value={item.price}
                                    onChange={(e) =>
                                      handleShPriceChange(
                                        item,
                                        Number(e.target.value)
                                      )
                                    }
                                    className="w-full px-2 py-1 border-none bg-transparent outline-none rounded"
                                  />
                                ) : [
                                    "payable",
                                    "cr",
                                    "dr",
                                    "price",
                                    "paid",
                                    "total",
                                  ].includes(dt.id) ? (
                                  `${Number(item[dt.id]).toLocaleString(
                                    "en-PK"
                                  )}`
                                ) : dt.id === "companyId" ? (
                                  item.companyId.name
                                ) : dt.id === "date" ? (
                                  formatDateTime(item[dt.id])
                                ) : (
                                  item[dt.id]
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 my-4">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <FigureCard
              title={"Total Box"}
              value={`${Number(
                CurrentSummaryTab === "All"
                  ? CustomerItemSummaryState?.data?.data
                      ?.filter((dt) => dt.type === "Box")
                      .reduce((acc, dt) => {
                        return acc + dt.qty;
                      }, 0) || 0
                  : CustomerItemSummaryState?.data?.shdata
                      ?.filter((dt) => dt.type === "Box")
                      .reduce((acc, dt) => {
                        return acc + dt.qty;
                      }, 0) || 0
              ).toLocaleString("en-PK")}`}
            />
            <FigureCard
              title={"Total Shopping"}
              value={`${Number(
                totalShoppingAmount || 0
              ).toLocaleString("en-PK")}`}
                  textColor={"text-success"}
            />
            <FigureCard
              title={"Opening Balance"}
              value={`Rs ${Number(
                Customers?.data?.find((dt) => dt._id === id)?.opening_balance ||
                  0
              ).toLocaleString("en-PK")}`}
                  textColor={"text-success"}
            />
            <FigureCard
              title={"Discount Amount"}
              value={`Rs ${Number(
                Customers?.data?.find((dt) => dt._id === id)?.discount || 0
              ).toLocaleString("en-PK")}`}
                  textColor={"text-success"}
            />
            <FigureCard
              title={"Return Amount"}
              value={`Rs ${Number(
                Customers?.data?.find((dt) => dt._id === id)?.return_amount || 0
              ).toLocaleString("en-PK")}`}
                  textColor={"text-success"}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-1 gap-2 sm:gap-3">
            <FigureCard
              title={"Total Amount"}
              value={`Rs ${Number(totalAmount || 0).toLocaleString("en-PK")}`}
            />

            <FigureCard
              title={"Paid Amount"}
              value={`Rs ${Number(
                Customers?.data?.find((dt) => dt._id === id)?.recieved || 0
              ).toLocaleString("en-PK")}`}
                  textColor={"text-success"}
            />
            <FigureCard
              title={"Remaining Amount"}
              value={`Rs ${Number(totalReciveableAmount || 0).toLocaleString(
                "en-PK"
              )}`}
                  textColor={"text-danger"}
            />
          </div>
        </div>
      </div>

      <div className="w-full max-w-5xl mt-6 px-2">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-bold text-ink">Saved Summary Records</h2>
            <p className="text-xs text-muted">
              Open loads that saved entry into the table above (editable). Live
              data returns via &quot;Back to live summary&quot;.
            </p>
          </div>
          <button
            type="button"
            onClick={loadSavedRecords}
            className="text-xs font-bold text-main hover:underline"
          >
            Refresh
          </button>
        </div>

        {recordsLoading ? (
          <p className="text-sm text-muted py-6 text-center">Loading records…</p>
        ) : savedRecords.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-subtle px-4 py-8 text-center text-sm text-muted">
            No saved records yet. Edit prices, then click{" "}
            <strong>Save Record</strong>.
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-xl border border-line bg-card shadow-soft">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-main text-white">
                <tr>
                  <th className="border border-main px-3 py-2 text-left">Date</th>
                  <th className="border border-main px-3 py-2 text-left">Tab</th>
                  <th className="border border-main px-3 py-2 text-right">Total</th>
                  <th className="border border-main px-3 py-2 text-right">
                    Remaining
                  </th>
                  <th className="border border-main px-3 py-2 text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {savedRecords.map((record) => (
                  <tr
                    key={record._id}
                    className="font-quicksand hover:bg-subtle/70 transition-colors"
                  >
                    <td className="border border-line px-3 py-2 text-inkSecondary whitespace-nowrap">
                      {formatRecordWhen(record)}
                    </td>
                    <td className="border border-line px-3 py-2 text-ink">
                      {record.tab || "All"}
                    </td>
                    <td className="border border-line px-3 py-2 text-right tabular-nums font-semibold text-ink">
                      {formatRs(record.totalAmount)}
                    </td>
                    <td className="border border-line px-3 py-2 text-right tabular-nums font-semibold text-danger">
                      {formatRs(record.remainingAmount)}
                    </td>
                    <td className="border border-line px-3 py-2">
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <button
                          type="button"
                          title="Open in editable table above"
                          onClick={() => handleOpenRecord(record)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-bold transition-colors ${
                            activeSavedId === record._id
                              ? "border-main bg-main text-white"
                              : "border-main text-main hover:bg-main hover:text-white"
                          }`}
                        >
                          <MdOpenInNew size={14} />
                          Open
                        </button>
                        <button
                          type="button"
                          title="Download PDF"
                          onClick={() => handleDownloadRecordFromTable(record)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-danger text-danger text-xs font-bold hover:bg-danger hover:text-white transition-colors"
                        >
                          <FaFilePdf size={12} />
                          Download
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => handleDeleteRecord(record)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-line text-muted text-xs font-bold hover:border-danger hover:text-danger transition-colors"
                        >
                          <MdDelete size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        <CustomerItemSummaryA4Document
          ref={a4PrintRef}
          customer={{
            name: customerRecord?.name,
            contact:
              customerRecord?.contact != null
                ? String(customerRecord.contact)
                : "",
            address: customerRecord?.address,
          }}
          rows={a4Rows}
          tabLabel={CurrentSummaryTab}
          stats={a4Stats}
        />
        {selectedRecord && (
          <CustomerItemSummaryA4Document
            ref={recordPrintRef}
            customer={{
              name: selectedRecord.customerSnapshot?.name,
              contact: selectedRecord.customerSnapshot?.contact || "",
              address: selectedRecord.customerSnapshot?.address || "",
            }}
            rows={selectedRecord.rows || []}
            tabLabel={selectedRecord.tab || "All"}
            stats={selectedRecord.stats || {}}
          />
        )}
      </div>

      {openRecordModal && selectedRecord && (
        <CustomModal open={openRecordModal} setOpen={setOpenRecordModal}>
          <div className="p-4 sm:p-6 max-w-[860px]">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl font-bold text-ink">
                  Saved Item Summary
                </h2>
                <p className="text-xs text-muted mt-1">
                  {formatRecordWhen(selectedRecord)} · Tab{" "}
                  {selectedRecord.tab || "All"} ·{" "}
                  {formatRs(selectedRecord.totalAmount)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handlePrintRecord()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-ink bg-ink text-white text-xs font-bold"
                >
                  <MdPrint size={16} />
                  Print
                </button>
                <button
                  type="button"
                  disabled={recordPdfLoading}
                  onClick={handleDownloadRecordPdf}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-danger bg-danger text-white text-xs font-bold disabled:opacity-60"
                >
                  <FaFilePdf size={14} />
                  {recordPdfLoading ? "PDF…" : "Download"}
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-[70vh] border border-line rounded-xl">
              <CustomerItemSummaryA4Document
                customer={{
                  name: selectedRecord.customerSnapshot?.name,
                  contact: selectedRecord.customerSnapshot?.contact || "",
                  address: selectedRecord.customerSnapshot?.address || "",
                }}
                rows={selectedRecord.rows || []}
                tabLabel={selectedRecord.tab || "All"}
                stats={selectedRecord.stats || {}}
              />
            </div>
          </div>
        </CustomModal>
      )}

      <ThermalPrintHost
        data={thermalPrintData}
        printRef={thermalPrintRef}
        component={ThermalItemSummary}
      />
    </div>
  );
};

export default CustomersItemSummaryReport;
